/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef, Suspense } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  CircularProgress,
  Alert,
  AlertTitle,
  Button,
  useTheme,
  useMediaQuery,
  Snackbar
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axios from 'axios';

// 自社コンポーネント
import Header from '../../components/Header';
import ClientInfoCard from './ClientDetail/ClientInfoCard';
import ActionButtons from './ClientDetail/ActionButtons';
import ProductTabs from './ClientDetail/ProductTabs';
import ProductDetails from './ClientDetail/ProductDetails';
import ProductCreatePrompt from './ClientDetail/ProductCreatePrompt';
import EmptyProductState from './ClientDetail/EmptyProductState';

// ダイアログ類
import UpdateRemainingDialog from './ClientDetail/UpdateRemainingDialog';
import ClientEditDialog from './ClientDetail/ClientEditDialog';
import ClientDeleteDialog from './ClientDetail/ClientDeleteDialog';
import ProductEditDialog from './ClientDetail/ProductEditDialog';
import NoteEditDialog from './ClientDetail/NoteEditDialog';

import './ClientDetail.css';

// 🔧 修正: 価格情報コンポーネントを動的インポートに変更（循環参照回避）
const PriceInfoDisplayLazy = React.lazy(() => 
  import('./ClientDetail/trading/PriceInfo').then(module => ({ 
    default: module.PriceInfoDisplay 
  }))
);

// 🆕 価格編集ダイアログを動的インポート
const PriceCreateDialogLazy = React.lazy(() => 
  import('./ClientDetail/trading/PriceCreateDialog').then(module => ({ 
    default: module.PriceCreateDialog 
  }))
);

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // 基本データ
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  // 製品関連データ
  const [clientProducts, setClientProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedClientProductId, setSelectedClientProductId] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(null);

  // ダイアログ状態
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [clientEditOpen, setClientEditOpen] = useState(false);
  const [productEditOpen, setProductEditOpen] = useState(false);
  const [noteEditOpen, setNoteEditOpen] = useState(false);

  // 🆕 価格編集ダイアログ状態
  const [priceCreateOpen, setPriceCreateOpen] = useState(false);

  // UI状態
  const [tabValue, setTabValue] = useState(0);
  const [isNewRegistration, setIsNewRegistration] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const initDoneRef = useRef(false);

  // 🆕 価格情報用の状態（予測未設定時の価格表示用）
  const [priceRefreshTrigger, setPriceRefreshTrigger] = useState(0);

  /* ------------------------------------------------------------------ */
  /* 🚀 修正: sessionStorageから製品選択状態を取得                       */
  /* ------------------------------------------------------------------ */
  const getSelectedProductIdFromStorage = () => {
    try {
      const stored = sessionStorage.getItem("clientFilters_v2");
      if (!stored) return null;

      const parsed = JSON.parse(stored);
      
      // タイムスタンプチェック（30分以内のみ有効）
      const EXPIRY_TIME = 20 * 60 * 1000; // 20分
      if (!parsed.timestamp || Date.now() - parsed.timestamp > EXPIRY_TIME) {
        return null;
      }

      const productId = parsed.selectedProductId;
      
      // 型安全性チェック
      if (productId === "all") {
        return null; // "all"の場合は製品選択なしとして扱う
      } else if (typeof productId === "number" && !isNaN(productId)) {
        return productId;
      } else if (typeof productId === "string" && productId !== "null" && productId !== "") {
        const numericId = Number(productId);
        return !isNaN(numericId) ? numericId : null;
      }
      
      return null;
    } catch (error) {
      return null;
    }
  };

  // 🚀 修正: URLパラメータは廃止、sessionStorageから取得
  const getProductIdFromUrl = () => {
    // 🚀 重要: URLパラメータは使用しない
    // sessionStorageから製品選択状態を取得
    return getSelectedProductIdFromStorage();
  };

  const filterActiveProducts = (productsArray) => {
    if (!Array.isArray(productsArray)) return [];
    const filtered = productsArray.filter(product => 
      product && 
      product.id && 
      product.name && 
      product.active !== false
    );
    return filtered;
  };

  const filterValidClientProducts = (clientProductsArray, activeProductsArray) => {
    if (!Array.isArray(clientProductsArray) || !Array.isArray(activeProductsArray)) {
      return [];
    }
    
    const activeProductIds = new Set(activeProductsArray.map(p => p.id));
    
    const filtered = clientProductsArray.filter(cp => 
      cp && 
      cp.id && 
      cp.productId && 
      activeProductIds.has(cp.productId)
    );
    
    return filtered;
  };

  const findProductById = (productId, productsArray = products) => {
    if (!productId || !Array.isArray(productsArray)) return null;
    const found = productsArray.find(p => p && p.id === productId) || null;
    return found;
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  /* ------------------------------------------------------------------ */
  /* API 通信関数                                                       */
  /* ------------------------------------------------------------------ */
  const fetchClient = async () => {
    const token = sessionStorage.getItem('token');
    try {
      const res = await axios.get(`/api/clients/${id}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setClient(res.data);
      return res.data;
    } catch (err) {
      if (err.response?.status === 404) {
        setErrorMessage('この顧客は削除された可能性があります。一覧画面に戻ってください。');
      } else {
        setErrorMessage('顧客情報の取得に失敗しました。');
      }
      setClient(null);
      return null;
    }
  };

  const fetchClientProducts = async () => {
    const token = sessionStorage.getItem('token');
    try {
      const res = await axios.get(`/api/client-products?clientId=${id}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      
      const rawClientProducts = res.data || [];
      setClientProducts(rawClientProducts);
      return rawClientProducts;
    } catch (err) {
      setClientProducts([]);
      return [];
    }
  };

  const fetchProducts = async () => {
    const token = sessionStorage.getItem('token');
    try {
      const res = await axios.get('/api/products', { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      
      const activeProducts = filterActiveProducts(res.data || []);
      setProducts(activeProducts);
      return activeProducts;
    } catch (err) {
      setProducts([]);
      return [];
    }
  };

  /* ------------------------------------------------------------------ */
  /* 初期データ取得                                                     */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    Promise.all([fetchClient(), fetchClientProducts(), fetchProducts()]).then(() => {
      if (sessionStorage.getItem('fromNewRegistration') === 'true') {
        setIsNewRegistration(true);
        sessionStorage.removeItem('fromNewRegistration');
      }
      setLoading(false);
    });
  }, [id, navigate]);

  /* ------------------------------------------------------------------ */
  /* 🚀 修正: 製品とタブの初期設定（sessionStorage対応）                 */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (loading || products.length === 0 || initDoneRef.current) return;

    const validClientProducts = filterValidClientProducts(clientProducts, products);
    
    // 🚀 修正: sessionStorageから製品選択状態を取得
    const storedProductId = getSelectedProductIdFromStorage();

    if (storedProductId && findProductById(storedProductId)) {
      // 🚀 一覧画面で選択されていた製品がある場合
      const productIndex = products.findIndex(p => p.id === storedProductId);
      const correspondingClientProduct = validClientProducts.find(cp => cp.productId === storedProductId);
      
      if (productIndex !== -1) {
        setTabValue(productIndex);
        setSelectedProductId(storedProductId);
        
        if (correspondingClientProduct) {
          setSelectedClientProductId(correspondingClientProduct.id);
          setSelectedProduct(correspondingClientProduct);
        } else {
          setSelectedClientProductId(null);
          setSelectedProduct(null);
        }
      } else {
        // 保存された製品IDが無効な場合はデフォルト選択
        handleDefaultProductSelection(validClientProducts);
      }
    } else {
      // 保存された製品選択がない場合はデフォルト選択
      handleDefaultProductSelection(validClientProducts);
    }

    initDoneRef.current = true;
  }, [products, clientProducts, loading]);

  const handleDefaultProductSelection = (validClientProducts = null) => {
    const validCPs = validClientProducts || filterValidClientProducts(clientProducts, products);
    
    if (validCPs.length > 0) {
      const firstValidCP = validCPs[0];
      const productIndex = products.findIndex(p => p.id === firstValidCP.productId);
      
      setSelectedClientProductId(firstValidCP.id);
      setSelectedProduct(firstValidCP);
      setSelectedProductId(firstValidCP.productId);
      setTabValue(Math.max(0, productIndex));
    } else if (products.length > 0) {
      setTabValue(0);
      setSelectedProductId(products[0].id);
      setSelectedClientProductId(null);
      setSelectedProduct(null);
    } else {
      setTabValue(0);
      setSelectedProductId(null);
      setSelectedClientProductId(null);
      setSelectedProduct(null);
    }
  };

  /* ------------------------------------------------------------------ */
  /* タブ or 製品選択                                                   */
  /* ------------------------------------------------------------------ */
  const handleProductChange = (clientProductId) => {
    const validClientProducts = filterValidClientProducts(clientProducts, products);
    const cp = validClientProducts.find(c => c.id === clientProductId);
    
    if (cp && findProductById(cp.productId)) {
      setSelectedClientProductId(clientProductId);
      setSelectedProduct(cp);
      setSelectedProductId(cp.productId);
    }
  };

  const handleTabChange = (e, newIdx) => {
    if (newIdx < 0 || newIdx >= products.length) return;
    
    setTabValue(newIdx);
    const product = products[newIdx];
    if (!product || !findProductById(product.id)) {
      return;
    }

    const validClientProducts = filterValidClientProducts(clientProducts, products);
    const correspondingClientProduct = validClientProducts.find(cp => cp.productId === product.id);
    
    if (correspondingClientProduct) {
      handleProductChange(correspondingClientProduct.id);
    } else {
      setSelectedProductId(product.id);
      setSelectedClientProductId(null);
      setSelectedProduct(null);
    }

    // 🚀 修正: 詳細画面でのタブ変更はURLを更新しない
    // 一覧画面の製品選択状態は変更しない
  };

  /* ------------------------------------------------------------------ */
  /* 🆕 修正版：データ更新時の共通処理 - 機能設定更新対応                */
  /* ------------------------------------------------------------------ */
  const refreshClientProductData = async () => {
    try {
      const updatedClientProducts = await fetchClientProducts();
      
      const validClientProducts = filterValidClientProducts(updatedClientProducts, products);
      
      if (selectedClientProductId) {
        const updatedProduct = validClientProducts.find(cp => cp.id === selectedClientProductId);
        
        if (updatedProduct && findProductById(updatedProduct.productId)) {
          // 🔹 機能設定更新のため、selectedProductを強制的に更新
          // Reactの参照比較をトリガーするために新しいオブジェクトを作成
          const refreshedProduct = { 
            ...updatedProduct,
            _refreshTimestamp: Date.now() // 🔹 強制更新用のタイムスタンプ
          };
          
          setSelectedProduct(refreshedProduct);
        } else {
          handleDefaultProductSelection(validClientProducts);
        }
      }
      
      // 🆕 価格情報の強制更新（予測未設定時用）
      setPriceRefreshTrigger(prev => prev + 1);
      
      return validClientProducts;
    } catch (error) {
      showSnackbar('製品データの更新に失敗しました', 'error');
      return [];
    }
  };

  const refreshClientData = async () => {
    try {
      const updatedClient = await fetchClient();
      return updatedClient;
    } catch (error) {
      showSnackbar('顧客データの更新に失敗しました', 'error');
      return null;
    }
  };

  /* ------------------------------------------------------------------ */
  /* 🚀 修正：製品設定の新規作成 - ProductEditDialog表示条件修正        */
  /* ------------------------------------------------------------------ */
  const handleCreateProductSetting = async (productData = {}) => {
    if (!selectedProductId || !client) return;
    
    if (!findProductById(selectedProductId)) {
      showSnackbar('選択された製品が無効です。ページを再読み込みしてください。', 'error');
      return;
    }
    
    try {
      const token = sessionStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const requestData = {
        ...productData,
        clientId: client.id,
        productId: selectedProductId
      };

      const { data: newCp } = await axios.post('/api/client-products', requestData, { headers });

      const actualProductId = newCp.productId;
      
      if (!findProductById(actualProductId)) {
        throw new Error('作成された製品設定の製品が無効です');
      }
      
      const tabIdx = products.findIndex(p => p.id === actualProductId);

      setClientProducts(prev => [...prev, newCp]);
      setSelectedClientProductId(newCp.id);
      setSelectedProduct(newCp);
      setSelectedProductId(actualProductId);
      if (tabIdx !== -1) setTabValue(tabIdx);

      setIsNewRegistration(false);

      // 🚀 成功メッセージに機能状態を含める
      const functionStatus = newCp.tankInstalled && newCp.predictionEnabled ? 
        '（タンク機能・予測機能が有効になりました）' :
        newCp.predictionEnabled ? '（予測機能が有効になりました）' :
        '（基本設定で作成されました）';
      
      showSnackbar(`製品設定を作成しました ${functionStatus}`, 'success');

      // 🚀 修正：ウィザード完了時はProductEditDialogを表示しない
      // ウィザード経由では必要な機能フラグが設定されているため、追加のダイアログは不要
      const isWizardCompletion = Boolean(
        productData.predictionEnabled !== undefined // 予測機能設定がある = ウィザード経由
      );
      
      // ウィザード経由でない場合のみ編集ダイアログを表示（現在は該当ケースなし）
      if (!isWizardCompletion && !productData.totalCapacity && newCp.tankInstalled) {
        setProductEditOpen(true);
      }
      
    } catch (error) {
      let message = "製品設定の作成に失敗しました。もう一度お試しください。";
      if (error.response?.data && typeof error.response.data === "string") {
        message = error.response.data;
      } else if (error.message) {
        message = `エラー: ${error.message}`;
      }
      showSnackbar(message, 'error');
    }
  };

  /* ------------------------------------------------------------------ */
  /* 🚀 修正: 戻るボタン（一覧画面の製品選択状態を保持）                  */
  /* ------------------------------------------------------------------ */
  const handleGoBack = () => {
    // 🚀 重要: sessionStorageの製品選択状態はそのまま保持
    // 顧客詳細画面で変更したタブ状態は一覧画面に反映しない
    navigate("/clientList");
  };

  /* ------------------------------------------------------------------ */
  /* 備考編集関連                                                       */
  /* ------------------------------------------------------------------ */
  const handleEditNote = () => {
    setNoteEditOpen(true);
  };

  const handleNoteUpdate = async () => {
    await refreshClientData();
    setNoteEditOpen(false);
  };

  /* ------------------------------------------------------------------ */
  /* 連絡先更新関連（ClientInfoCard用）                                  */
  /* ------------------------------------------------------------------ */
  const handleContactUpdate = async () => {
    await refreshClientData();
    showSnackbar('連絡先情報を更新しました', 'success');
  };

  /* ------------------------------------------------------------------ */
  /* 🆕 ProductDetails用メッセージ表示ハンドラー                        */
  /* ------------------------------------------------------------------ */
  const handleShowMessage = (message, severity = 'success') => {
    showSnackbar(message, severity);
  };

  /* ------------------------------------------------------------------ */
  /* 🆕 価格編集ハンドラー（統合版）                                    */
  /* ------------------------------------------------------------------ */
  const handlePriceEdit = (editData) => {
    const { mode, selectedProduct: editProduct, productId, clientId } = editData || {};
    
    if (mode === 'create') {
      // 🆕 新規作成モード：中間テーブル未作成時
      if (productId && clientId) {
        setPriceCreateOpen(true);
      } else {
        showSnackbar('必要な情報が不足しています', 'error');
      }
    } else if (mode === 'edit') {
      // 既存編集モード：ProductDetailsの価格セクションで処理
    } else {
      // フォールバック：予測未設定時用
      if (selectedProductId && client?.id) {
        setPriceCreateOpen(true);
      }
    }
  };

  /* ------------------------------------------------------------------ */
  /* 🆕 価格作成成功時の処理                                           */
  /* ------------------------------------------------------------------ */
  const handlePriceCreateSuccess = async (message) => {
    showSnackbar(message, 'success');
    
    // 🔹 データを更新して新しい中間テーブルを反映
    await refreshClientProductData();
    
    // 🔹 作成された中間テーブルを選択状態にする
    const updatedClientProducts = await fetchClientProducts();
    const validClientProducts = filterValidClientProducts(updatedClientProducts, products);
    const newClientProduct = validClientProducts.find(cp => 
      cp.clientId === Number(id) && 
      cp.productId === selectedProductId
    );
    
    if (newClientProduct) {
      setSelectedClientProductId(newClientProduct.id);
      setSelectedProduct(newClientProduct);
    }
    
    setPriceCreateOpen(false);
  };

  const handlePriceCreateError = (message) => {
    showSnackbar(message, 'error');
    setPriceCreateOpen(false);
  };

  /* ------------------------------------------------------------------ */
  /* LOADING / ERROR                                                    */
  /* ------------------------------------------------------------------ */
  if (loading) {
    return (
      <>
        <Header />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (!client) {
    return (
      <>
        <Header />
        <Container maxWidth="md" sx={{ mt: 4 }}>
          <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
            <AlertTitle>エラー</AlertTitle>
            {errorMessage || '顧客情報の取得に失敗しました。'}
          </Alert>
          <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={handleGoBack}>
            戻る
          </Button>
        </Container>
      </>
    );
  }

  const validClientProducts = filterValidClientProducts(clientProducts, products);

  /* ------------------------------------------------------------------ */
  /* MAIN RENDER                                                        */
  /* ------------------------------------------------------------------ */
  return (
    <>
      <Header />

      <Container maxWidth="md" sx={{ pt: 3, pb: 8 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleGoBack} size={isMobile ? 'small' : 'medium'}>
            顧客一覧に戻る
          </Button>
          <ActionButtons onEdit={() => setClientEditOpen(true)} onDelete={() => setDeleteDialogOpen(true)} />
        </Box>

        {isNewRegistration && (
          <Alert severity="success" sx={{ mb: 3 }}>
            顧客情報の登録が完了しました。次に製品情報を設定してください。
          </Alert>
        )}

        <ClientInfoCard 
          client={client} 
          onEditNote={handleEditNote}
          onContactUpdate={handleContactUpdate}
        />

        {products.length > 0 ? (
          <>
            <ProductTabs
              products={products}
              clientProducts={validClientProducts}
              tabValue={tabValue}
              onTabChange={handleTabChange}
              onProductChange={handleProductChange}
            />

            {selectedProduct ? (
              // ケース1: 予測設定済み（従来通り）
              <>
                <ProductDetails
                  selectedProduct={selectedProduct}
                  onUpdateClick={() => setUpdateModalOpen(true)}
                  onEditClick={() => setProductEditOpen(true)}
                  onShowMessage={handleShowMessage}
                  onFunctionUpdate={refreshClientProductData} // 🆕 機能更新用コールバック追加
                />
              </>
            ) : selectedProductId && findProductById(selectedProductId) ? (
              // 🆕 ケース2: 予測未設定 + 製品情報あり（価格情報を追加表示）
              <>
                {/* 🆕 価格情報を先に表示（Suspenseでラップ） */}
                <Suspense fallback={<CircularProgress />}>
                  <PriceInfoDisplayLazy
                    selectedProduct={null}
                    productId={selectedProductId}
                    clientId={Number(id)}
                    onPriceEdit={handlePriceEdit}
                    refreshTrigger={priceRefreshTrigger}
                  />
                </Suspense>
                
                {/* 製品設定促進（従来通り） */}
                <ProductCreatePrompt
                  productName={findProductById(selectedProductId)?.name}
                  onCreateClick={handleCreateProductSetting}
                  clientId={client.id}
                  productId={selectedProductId}
                  isHighlighted={isNewRegistration}
                />
              </>
            ) : (
              // ケース3: 製品未登録（従来通り）
              <>
                <EmptyProductState onEditClick={() => setClientEditOpen(true)} />
              </>
            )}
          </>
        ) : (
          <>
            <EmptyProductState onEditClick={() => setClientEditOpen(true)} />
          </>
        )}
      </Container>

      <UpdateRemainingDialog
        open={updateModalOpen}
        onClose={() => setUpdateModalOpen(false)}
        clientProduct={selectedProduct}
        selectedClientProductId={selectedClientProductId}
        onUpdate={refreshClientProductData}
      />

      <ClientEditDialog
        open={clientEditOpen}
        onClose={() => setClientEditOpen(false)}
        client={client}
        id={id}
      />

      <ClientDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        clientId={id}
      />
      
      <ProductEditDialog
        open={productEditOpen}
        onClose={() => setProductEditOpen(false)}
        clientProduct={selectedProduct}
        selectedClientProductId={selectedClientProductId}
        onUpdate={refreshClientProductData}
      />

      <NoteEditDialog
        open={noteEditOpen}
        onClose={() => setNoteEditOpen(false)}
        client={client}
        onUpdate={handleNoteUpdate}
      />

      {/* 🆕 価格新規作成ダイアログ */}
      {priceCreateOpen && (
        <Suspense fallback={<CircularProgress />}>
          <PriceCreateDialogLazy
            open={priceCreateOpen}
            onClose={() => setPriceCreateOpen(false)}
            clientId={Number(id)}
            productId={selectedProductId}
            onSuccess={handlePriceCreateSuccess}
            onError={handlePriceCreateError}
          />
        </Suspense>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}